import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '../../components/Footer';
import Menu from '../../components/Menu/Menu';
import SelectCountry from '../../components/SelectCountry';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { getLangCountry } from '../../helpers/languages';
import { useRouter } from 'next/router';
import {
  fetchShippingCountriesList,
  getShippingRate
} from '../../fetchers/shipping';
import { formatPrice } from '../../helpers/prices';
import { useAlert } from '../../context/AlertProvider';
import Spinner from '../../components/Spinner';
import { ICountryListItem } from '../../models/footer';
import MyHead from '../../components/MyHead';

interface ICountryData {
  title: string;
  cost: number;
  asterisk: string;
}

const ShippingPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const alert = useAlert();
  const router = useRouter();
  const { lang, country } = getLangCountry(router.query);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countryData, setCountryData] = useState<ICountryData | null>(null);
  const shippingCountriesList = fetchShippingCountriesList({ lang });
  const [currentShippingCountry, setCurrentShippingCountry] =
    useState<ICountryListItem | null>(null);

  useEffect(() => {
    if (router.query.lang && country && !selectedCountry) {
      setSelectedCountry(country);
    }
  }, [router.query.lang]);

  useEffect(() => {
    if (router.query?.lang && selectedCountry) {
      const actualCountry = localStorage.getItem('jeep_actual_country');

      let country: string;

      if (!actualCountry) {
        country = selectedCountry;
      } else if (
        (selectedCountry === 'eu' || selectedCountry === 'int') &&
        actualCountry
      ) {
        country = actualCountry;
      } else {
        country = selectedCountry;
      }

      const currentShippingCountry = shippingCountriesList.find(c => {
        return c['country-code']?.toLowerCase() === country.toLowerCase();
      })!;

      setCurrentShippingCountry(currentShippingCountry);
    }
  }, [selectedCountry, router.query.lang]);

  useEffect(() => {
    (async () => {
      if (!selectedCountry) {
        return;
      }
      setCountryData(null);
      setLoading(true);

      const { data, error } = await getShippingRate({
        lang,
        country: selectedCountry
      });

      setLoading(false);

      if (error) {
        return alert.error(t('GENERIC_ERROR'));
      }

      setCountryData(data);
    })();
  }, [selectedCountry]);

  const { title = '', cost = 0, asterisk = '' } = countryData || {};

  return (
    <>
      <MyHead />
      <div>
        <Menu />
        <div
          className="h-500 flex justify-center items-end py-3"
          style={{
            background: `linear-gradient(to bottom, rgba(0, 20, 26, 0.1), rgba(0, 20, 26, 0.25)), url("../../img/general-page-img.jpg")`
          }}
        >
          <h1 className="text-8xl sm:text-9xl text-white uppercase mb-6">
            {t('SHIPPINGS')}
          </h1>
        </div>
        <div className="flex items-stretch flex-col text-center pt-20 max-w-4xl mx-auto px-4 sm:px-0">
          <p className="mb-6">{t('SHIPPING_PAGE_SELECT')}</p>
          <div className="mx-auto w-80 sm:w-96">
            <SelectCountry
              country={selectedCountry}
              currentShippingCountry={currentShippingCountry}
              onChange={setSelectedCountry}
            />
          </div>
          <div className="mt-16">
            <TabGroup>
              <TabList>
                <Tab>
                  {({ selected }) => (
                    <button
                      className={
                        selected
                          ? 'text-black font-bold mr-20 pb-3'
                          : 'opacity-60 font-bold mr-20 pb-3'
                      }
                    >
                      {t('SHIPPINGS')}
                    </button>
                  )}
                </Tab>
                <Tab>
                  {({ selected }) => (
                    <button
                      className={
                        selected
                          ? 'text-black font-bold pb-3'
                          : 'opacity-60 font-bold pb-3'
                      }
                    >
                      {t('RETURNS')}
                    </button>
                  )}
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  <div className="border-t-2 border-gray-100 py-10 flex flex-col items-center">
                    {loading ? (
                      <Spinner />
                    ) : (
                      <>
                        <div className="flex flex-col items-center bg-modalgrey py-3 px-10 rounded-md max-w-2xl">
                          <h3 className="font-bold text-2xl">
                            {formatPrice(
                              cost,
                              shippingCountriesList,
                              currentShippingCountry
                                ? currentShippingCountry['currency']
                                : ''
                            )}{' '}
                            *
                          </h3>
                          <p className="opacity-60 font-bold mt-1">{title}</p>
                          <p className="opacity-60 mt-6">
                            {asterisk && `* ${asterisk}`}
                          </p>
                        </div>
                        <div className="py-10 text-left">
                          <p className="font-bold mb-10">
                            {t('EXTRA_UE_TEXT')}
                          </p>
                          <p className="mb-10">{t('SHIPPING_TEXT')}</p>
                          <p className="font-bold mb-2">
                            {t('SHIPPING_DELAY_TITLE')}
                          </p>
                          <p className="mb-10">{t('SHIPPING_DELAY_TEXT')}</p>
                          <p className="font-bold mb-2">
                            {t('CARRYING_TITLE')}
                          </p>
                          <p className="mb-10">{t('CARRYING_TEXT')}</p>
                        </div>
                      </>
                    )}
                  </div>
                </TabPanel>
                <TabPanel>
                  <div className="py-10 text-left border-t-2 border-gray-100">
                    <p className="font-bold mb-10">{t('RETURNS_SUBTITLE')}</p>
                    <p className="font-bold mb-2">{t('RETURNS_1_TITLE')}</p>
                    <p className="mb-10">{t('RETURNS_1_TEXT')}</p>
                    <p className="font-bold mb-2">{t('RETURNS_2_TITLE')}</p>
                    <p className="mb-10">{t('RETURNS_2_TEXT')}</p>
                    <p className="font-bold mb-2">{t('RETURNS_3_TITLE')}</p>
                    <p className="mb-10">{t('RETURNS_3_TEXT')}</p>
                    <p className="font-bold mb-2">{t('RETURNS_4_TITLE')}</p>
                    <p className="mb-10">{t('RETURNS_4_TEXT')}</p>
                  </div>
                </TabPanel>
              </TabPanels>
            </TabGroup>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default ShippingPage;
